import React, { useState } from 'react';
import {
  GridRowModes,
  DataGridPro,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridPagination,
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector
} from '@mui/x-data-grid-pro';
import './style.scss';
import { tableStyle } from './Style';
import { randomId } from '../../util/util';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { ArticleOutlined, Delete, Edit, Visibility } from '@mui/icons-material';
import MuiPagination from '@mui/material/Pagination';

function Pagination({ page, onPageChange, className }) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event, newPage - 1);
      }}
    />
  );
}

function CustomPagination(props) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

const CustomTable = ({
  header,
  data,
  setData,
  numberOfRows = 50,
  sort,
  updateRowData,
  deleteRowData,
  viewRowData,
  hideFooter = false,
  action = ['none'],
  actionPosition = 'latest',
  hideHeader = false,
  rowHeight = 52,
  loading = false,
  clickToEdit = true,
  onCellClickWhenEditing = () => {},
  updateEditingModeFunction = null,
  rowClass,
  sx,
  columnsInitialState,
  allowScroll = true,
  Yscrollbar = false,
  ...props
}) => {
  const notifyEditingMode = (isEditing) => {
    if (updateEditingModeFunction) {
      updateEditingModeFunction(isEditing);
    }
  };

  const actions = [
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width:
        action.length === 2 || action.includes('edit') || action.includes('add-new')
          ? 80
          : 40 * action.length,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const listAction = [];

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{ color: 'primary.main' }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ];
        } else {
          if (action.includes('view')) {
            listAction.push(
              <GridActionsCellItem
                icon={<ArticleOutlined />}
                label="View"
                className="textPrimary"
                onClick={handleViewClick(row)}
                color="inherit"
              />
            );
          }
          if (action.includes('view-document')) {
            listAction.push(
              <GridActionsCellItem
                icon={<Visibility />}
                label="View-document"
                className="textPrimary"
                onClick={handleViewDocumentClick(row)}
                color="inherit"
              />
            );
          }
          if (action.includes('edit')) {
            listAction.push(
              <GridActionsCellItem
                icon={<Edit />}
                label="Edit"
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />
            );
          }
          if (action.includes('delete')) {
            listAction.push(
              <GridActionsCellItem
                icon={<Delete />}
                label="Delete"
                className="textPrimary"
                onClick={handleDeleteClick(id, row)}
                color="inherit"
              />
            );
          }
          if (action.includes('add-new') && row.isNew) {
            listAction.push(
              <GridActionsCellItem
                icon={<Edit />}
                label="Edit"
                className="textPrimary"
                onClick={handleEditClick(id)}
                color="inherit"
              />
            );
          }
        }
        return listAction;
      }
    }
  ];
  const safeHeader = Array.isArray(header) ? header : [];
  const safeAction = Array.isArray(action) ? action : ['none'];

  const columns = safeAction.includes('none')
    ? safeHeader
    : actionPosition === 'first'
      ? [...actions, ...safeHeader]
      : [...safeHeader, ...actions];

  const [rowModesModel, setRowModesModel] = useState({});

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
    notifyEditingMode(false);
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    notifyEditingMode(true);
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    notifyEditingMode(false);
  };

  const handleDeleteClick = (id, row) => () => {
    if (deleteRowData) deleteRowData(row);
    if (setData && Array.isArray(data)) {
      setData(data.filter((row) => row.id !== id));
    }
  };

  const handleViewClick = (row) => () => {
    if (viewRowData) viewRowData(row);
  };

  const handleViewDocumentClick = (row) => () => {
    if (row?.attachment?.downloadUrl) {
      window.open(row.attachment.downloadUrl, '_blank');
    } else {
      alert('File not found!');
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });

    if (Array.isArray(data)) {
      const editedRow = data.find((row) => row.id === id);
    }
    notifyEditingMode(false);
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow };
    if (setData && Array.isArray(data)) {
      setData(data.map((row) => (row.id === newRow.id ? updatedRow : row)));
    }
    if (updateRowData) updateRowData(updatedRow);
    notifyEditingMode(false);
    return updatedRow;
  };

  const handleRowModesModelChangeFunc = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleOnCellClick = (params, event) => {
    const isInEditMode = rowModesModel[params.row.id]?.mode === GridRowModes.Edit;
    if (isInEditMode) onCellClickWhenEditing(params, event);
  };

  function getRowId(row) {
    return row.id || randomId();
  }

  const safeData = Array.isArray(data) ? data : [];
  const HEADER_HEIGHT = hideHeader ? 0 : 56;
  const ROW_HEIGHT = rowHeight === 'auto' ? 56 : rowHeight;

  const shouldScroll = allowScroll && safeData.length > numberOfRows;
  const maxHeight = numberOfRows * ROW_HEIGHT + HEADER_HEIGHT;

  const shouldLimitHeight = safeData.length > numberOfRows;

  return (
    <div className="custom-table">
      <DataGridPro
        sx={{
          ...tableStyle,
          '& .MuiDataGrid-virtualScroller': {
            overflowY: shouldScroll ? 'auto' : 'hidden'
          },
          ...sx
        }}
        initialState={{
          ...columnsInitialState,
          ...sort
        }}
        rows={safeData}
        columns={columns}
        /** Infinite scroll settings */
        pagination={false}
        autoHeight={!shouldLimitHeight}
        style={shouldLimitHeight ? { height: maxHeight } : undefined}
        // /** Row handling */
        getRowHeight={() => rowHeight}
        getRowId={getRowId}
        /** Appearance */
        columnHeaderHeight={hideHeader ? 0 : 56}
        showCellVerticalBorder
        showColumnVerticalBorder
        hideFooter
        /** Editing */
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={clickToEdit ? handleRowModesModelChangeFunc : null}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onCellClick={handleOnCellClick}
        /** Performance */
        loading={loading}
        disableRowSelectionOnClick
        disableColumnMenu
        getRowClassName={typeof rowClass === 'function' ? rowClass : undefined}
        scrollbarSize={allowScroll ? undefined : 0}
        {...props}
      />
    </div>
  );
};

export default CustomTable;
